import { trainingFormStatus } from "../constants";
import { isUnemployedEmploymentType } from "./employmentTypeUtils";

/**
 * Bifurcation row on the Unemployed / None (unemployed) budget branch.
 * Membership is SETA employment_type only — department is ignored.
 */
export function isUnemployedAllocatedRow(item, employmentTypes) {
  if (!hasUnemployedTypesConfigured(employmentTypes)) return false;
  return isUnemployedEmploymentType(employmentTypes, item?.employment_type);
}

/** @deprecated Prefer isUnemployedAllocatedRow — kept for callers still using dept-based None. */
export function isNoneBranchAllocatedRow(item) {
  const idRaw = item.dept_id ?? item.department_id;
  if (idRaw == null || idRaw === "") return true;
  const id = String(idRaw).toLowerCase().trim();
  if (id === "none") return true;
  const name = String(item.dept_name ?? item.department_name ?? "")
    .toLowerCase()
    .trim();
  return name === "none" || name === "unemployed";
}

/**
 * B-BBEE chart "None (unemployed)" row head counts from bifurcation allocated_data.
 * Uses SETA unemployed employment_type only.
 */
function allocatedRowPersonKey(item, fallbackIndex) {
  if (item?.emp_id != null && item.emp_id !== "") return `e:${item.emp_id}`;
  if (item?.form_id != null && item.form_id !== "") return `f:${item.form_id}`;
  if (item?.training_id != null && item.training_id !== "") {
    return `t:${item.training_id}:${fallbackIndex ?? 0}`;
  }
  if (fallbackIndex != null) return `r:${fallbackIndex}`;
  return null;
}

function tallyAllocatedHeadcountByStatus(rows) {
  const recommendedKeys = new Set();
  const requestedKeys = new Set();
  const approvedKeys = new Set();
  const anyKeys = new Set();

  rows.forEach((item, index) => {
    const key = allocatedRowPersonKey(item, index);
    if (key == null) return;
    anyKeys.add(key);
    const st = String(
      item.status ?? item.form_status ?? trainingFormStatus.RECOMMENDED,
    ).trim();
    // Case-insensitive — optimiser rows / RPCs may vary casing.
    const stLower = st.toLowerCase();
    if (stLower === String(trainingFormStatus.APPROVED).toLowerCase()) {
      approvedKeys.add(key);
    } else if (stLower === String(trainingFormStatus.REQUESTED).toLowerCase()) {
      requestedKeys.add(key);
    } else if (
      stLower === String(trainingFormStatus.RECOMMENDED).toLowerCase() ||
      !stLower
    ) {
      recommendedKeys.add(key);
    } else {
      // Optimiser planned rows with unexpected status still count as recommended.
      recommendedKeys.add(key);
    }
  });

  // Same person must not sit in both Recommended and Approved (prevents
  // Optimiser Planned = rec+app double-count when mergeRecommendedApproved runs).
  approvedKeys.forEach((key) => recommendedKeys.delete(key));

  return {
    total: anyKeys.size,
    requested: requestedKeys.size,
    recommended: recommendedKeys.size,
    approved: approvedKeys.size,
  };
}

/** Unemployed by SETA employment_type, or emp_id in SETA unemployed key set. */
export function isUnemployedRow(item, employmentTypes, unemployedEmpKeys = null) {
  if (isUnemployedEmploymentType(employmentTypes, item?.employment_type)) {
    return true;
  }
  const eid = item?.emp_id;
  if (eid == null || eid === "" || !(unemployedEmpKeys instanceof Set)) {
    return false;
  }
  return (
    unemployedEmpKeys.has(`e:${eid}`) ||
    unemployedEmpKeys.has(`id:${eid}`) ||
    unemployedEmpKeys.has(String(eid))
  );
}

/**
 * Unique planned people (Recommended or Approved) by dept bucket.
 * Unemployed always map to "none" (employment_type / emp-key), not their dept_id.
 * @returns {Map<string, Set<string>>}
 */
export function buildPlannedPeopleByDeptFromAllocated(
  allocatedData,
  employmentTypes,
  unemployedEmpKeys = null,
) {
  /** @type {Map<string, Set<string>>} */
  const byDept = new Map();
  (allocatedData || []).forEach((item, index) => {
    const st = String(
      item.status ?? item.form_status ?? trainingFormStatus.RECOMMENDED,
    )
      .trim()
      .toLowerCase();
    if (st !== "recommended" && st !== "approved") return;
    const personKey = allocatedRowPersonKey(item, index);
    if (!personKey) return;
    const deptKey = isUnemployedRow(item, employmentTypes, unemployedEmpKeys)
      ? "none"
      : String(item.dept_id ?? "none")
          .trim()
          .toLowerCase() || "none";
    if (!byDept.has(deptKey)) byDept.set(deptKey, new Set());
    byDept.get(deptKey).add(personKey);
  });
  return byDept;
}

/** Count unique planned people for a department category (optional subtree). */
export function countPlannedPeopleForDept(
  peopleByDept,
  deptId,
  { isNone = false, nestedDeptIds = [] } = {},
) {
  if (!(peopleByDept instanceof Map)) return 0;
  if (isNone) return peopleByDept.get("none")?.size || 0;
  const ids = new Set(
    [deptId, ...(nestedDeptIds || [])]
      .map((id) => String(id ?? "").trim().toLowerCase())
      .filter(Boolean),
  );
  const people = new Set();
  ids.forEach((id) => {
    const bucket = peopleByDept.get(id);
    if (bucket) bucket.forEach((p) => people.add(p));
  });
  return people.size;
}

export function computeNoneBranchBbeeHeadcountFromAllocated(
  allocatedData,
  employmentTypes,
  unemployedEmpKeys = null,
) {
  if (!hasUnemployedTypesConfigured(employmentTypes)) {
    return { total: 0, requested: 0, recommended: 0, approved: 0 };
  }
  const rows = (allocatedData || []).filter((item) =>
    isUnemployedRow(item, employmentTypes, unemployedEmpKeys),
  );
  if (!rows.length) {
    return { total: 0, requested: 0, recommended: 0, approved: 0 };
  }
  return tallyAllocatedHeadcountByStatus(rows);
}

/**
 * Active company users flagged unemployed in SETA config — keys for emp_id and id.
 */
export async function fetchUnemployedEmpKeys(
  supabase,
  companyId,
  employmentTypes,
) {
  const keys = new Set();
  if (!hasUnemployedTypesConfigured(employmentTypes)) return keys;
  const { data, error } = await supabase
    .from("company_user")
    .select("id, emp_id, employment_type, role, batch_job_id")
    .eq("company_id", companyId)
    .eq("status", "active");
  if (error || !data?.length) return keys;
  data.forEach((r) => {
    if (!isCountedEmployeeRow(r)) return;
    if (!isUnemployedEmploymentType(employmentTypes, r.employment_type)) return;
    if (r.emp_id != null && r.emp_id !== "") {
      keys.add(`e:${r.emp_id}`);
      keys.add(String(r.emp_id));
    }
    if (r.id != null && r.id !== "") {
      keys.add(`id:${r.id}`);
      keys.add(String(r.id));
    }
  });
  return keys;
}

/**
 * B-BBEE None (unemployed) row amounts from allocated_data (employment type only).
 * Stipend is included once per form for planned statuses.
 */
export function computeUnemployedBbeeAmountsFromAllocated(
  allocatedData,
  employmentTypes,
  setaFields,
) {
  const empty = {
    noneRequested: 0,
    noneRecommended: 0,
    noneApproved: 0,
  };
  if (!hasUnemployedTypesConfigured(employmentTypes)) return empty;

  const rows = (allocatedData || []).filter((item) =>
    isUnemployedEmploymentType(employmentTypes, item.employment_type),
  );
  if (!rows.length) return empty;

  const costField = setaFields?.costField || "cost";
  let noneRequested = 0;
  let noneRecommended = 0;
  let noneApproved = 0;
  const stipendByForm = new Map();

  rows.forEach((item) => {
    const cost = Number(item[costField] ?? item.cost) || 0;
    // Blank/missing status on optimiser rows defaults to Recommended (SQL coalesce).
    const status =
      item.status ?? item.form_status ?? trainingFormStatus.RECOMMENDED;
    if (status === trainingFormStatus.REQUESTED) {
      noneRequested += cost;
    } else if (status === trainingFormStatus.RECOMMENDED) {
      noneRecommended += cost;
    } else if (status === trainingFormStatus.APPROVED) {
      noneApproved += cost;
    }

    const fid = item.form_id;
    if (fid != null) {
      const existing = stipendByForm.get(fid) ?? {
        amount: 0,
        requested: false,
        recommended: false,
        approved: false,
      };
      // Treat null/blank stipend as 0 — never drop the learner from programme cost.
      if (!existing.amount) {
        const stipendRaw = item.stipend_amount;
        existing.amount =
          stipendRaw == null || stipendRaw === ""
            ? 0
            : Number(stipendRaw) || 0;
      }
      if (status === trainingFormStatus.REQUESTED) existing.requested = true;
      if (status === trainingFormStatus.RECOMMENDED) existing.recommended = true;
      if (status === trainingFormStatus.APPROVED) existing.approved = true;
      stipendByForm.set(fid, existing);
    }
  });

  stipendByForm.forEach(({ amount, requested, recommended, approved }) => {
    if (!amount) return;
    // Prefer planned statuses for stipend (same as investment helpers)
    if (approved) noneApproved += amount;
    else if (recommended) noneRecommended += amount;
    else if (requested) noneRequested += amount;
  });

  return { noneRequested, noneRecommended, noneApproved };
}

/** Filter out unemployed learners from race/gender B-BBEE allocated rows. */
export function excludeUnemployedFromAllocated(allocatedData, employmentTypes) {
  if (!hasUnemployedTypesConfigured(employmentTypes)) {
    return allocatedData || [];
  }
  return (allocatedData || []).filter(
    (item) =>
      !isUnemployedEmploymentType(employmentTypes, item.employment_type),
  );
}

export const UNEMPLOYED_DEPT_ID = "unemployed";

/** Same SDF / import rule as dashboard demographics */
export function isCountedEmployeeRow(row) {
  const role = String(row?.role || "");
  return (
    !["sdf", "company_admin"].includes(role) || row?.batch_job_id != null
  );
}

/** Virtual / missing department (org / blank-dept only — not Unemployed budget). */
export function isNoneDepartmentRow(row) {
  const d = row?.department_id;
  if (d == null || d === "") return true;
  return String(d).toLowerCase() === "none";
}

export function hasUnemployedTypesConfigured(employmentTypes) {
  return (
    Array.isArray(employmentTypes) &&
    employmentTypes.some((e) => e?.isUnemployedType)
  );
}

/**
 * Count active company_user rows whose employment_type is flagged unemployed in SETA config.
 */
export async function fetchUnemployedLearnerCount(supabase, companyId, employmentTypes) {
  if (!hasUnemployedTypesConfigured(employmentTypes)) return 0;
  const { data, error } = await supabase
    .from("company_user")
    .select("id, employment_type, role, batch_job_id")
    .eq("company_id", companyId)
    .eq("status", "active");
  if (error || !data?.length) return 0;
  return data.filter(
    (r) =>
      isCountedEmployeeRow(r) &&
      isUnemployedEmploymentType(employmentTypes, r.employment_type),
  ).length;
}

/**
 * Single pass over active employees: total (demographics rule), employed vs unemployed (SETA isUnemployedType).
 * employed + unemployed === total for displayed counts.
 * unemployedOrNoneCount is aliased to unemployed (employment type only).
 */
export async function fetchEmploymentSnapshotCounts(supabase, companyId, employmentTypes) {
  const { data, error } = await supabase
    .from("company_user")
    .select("id, employment_type, role, batch_job_id, department_id")
    .eq("company_id", companyId)
    .eq("status", "active");
  if (error || !data) {
    return { total: 0, employed: 0, unemployed: 0, unemployedOrNoneCount: 0 };
  }
  const counted = data.filter((r) => isCountedEmployeeRow(r));
  const total = counted.length;
  const hasU = hasUnemployedTypesConfigured(employmentTypes);
  let unemployed = 0;
  let employed = total;
  if (hasU) {
    unemployed = counted.filter((r) =>
      isUnemployedEmploymentType(employmentTypes, r.employment_type),
    ).length;
    employed = Math.max(0, total - unemployed);
  }
  return { total, employed, unemployed, unemployedOrNoneCount: unemployed };
}

/**
 * Planned investment for unemployed learners: Recommended + Approved training cost,
 * plus stipend once per form when that form has Recommended or Approved rows.
 */
export function computeUnemployedRecommendedInvestment(
  allocatedData,
  employmentTypes,
  setaFields,
) {
  if (!hasUnemployedTypesConfigured(employmentTypes)) return 0;
  const rows = (allocatedData || []).filter((item) =>
    isUnemployedEmploymentType(employmentTypes, item.employment_type),
  );
  if (!rows.length) return 0;

  const costField = setaFields?.costField || "cost";
  let plannedTraining = 0;
  /** form_id -> { amount, includeStipend } */
  const stipendByForm = new Map();

  const isPlannedStatus = (status) =>
    status === trainingFormStatus.RECOMMENDED ||
    status === trainingFormStatus.APPROVED;

  rows.forEach((item) => {
    const cost = Number(item[costField] ?? item.cost) || 0;
    const status = item.status ?? item.form_status;
    const countsAsPlanned =
      costField !== "cost" || isPlannedStatus(status);
    if (countsAsPlanned) {
      plannedTraining += cost;
    }
    const fid = item.form_id;
    if (fid != null) {
      const existing = stipendByForm.get(fid) ?? {
        amount: 0,
        includeStipend: false,
      };
      if (!existing.amount) {
        existing.amount = Number(item.stipend_amount) || 0;
      }
      if (countsAsPlanned) {
        existing.includeStipend = true;
      }
      stipendByForm.set(fid, existing);
    }
  });

  let stipendTotal = 0;
  stipendByForm.forEach(({ amount, includeStipend }) => {
    if (includeStipend) stipendTotal += amount;
  });

  return plannedTraining + stipendTotal;
}

/**
 * @deprecated Dept-based None investment — prefer computeUnemployedRecommendedInvestment.
 */
export function computeNoneBranchPlannedInvestment(allocatedData, setaFields) {
  const rows = (allocatedData || []).filter(isNoneBranchAllocatedRow);
  if (!rows.length) return 0;

  const costField = setaFields?.costField || "cost";
  let plannedTraining = 0;
  const stipendByForm = new Map();

  const isPlannedStatus = (status) =>
    status === trainingFormStatus.RECOMMENDED ||
    status === trainingFormStatus.APPROVED;

  rows.forEach((item) => {
    const cost = Number(item[costField] ?? item.cost) || 0;
    const status = item.status ?? item.form_status;
    const countsAsPlanned =
      costField !== "cost" || isPlannedStatus(status);
    if (countsAsPlanned) {
      plannedTraining += cost;
    }
    const fid = item.form_id;
    if (fid != null) {
      const existing = stipendByForm.get(fid) ?? {
        amount: 0,
        includeStipend: false,
      };
      if (!existing.amount) {
        existing.amount = Number(item.stipend_amount) || 0;
      }
      if (countsAsPlanned) {
        existing.includeStipend = true;
      }
      stipendByForm.set(fid, existing);
    }
  });

  let stipendTotal = 0;
  stipendByForm.forEach(({ amount, includeStipend }) => {
    if (includeStipend) stipendTotal += amount;
  });

  return plannedTraining + stipendTotal;
}

/** Snapshot "Unemployed Planned" — employment type only for live and optimized. */
export function computeUnemployedPlannedInvestment(
  allocatedData,
  employmentTypes,
  setaFields,
  _sourceModule = "main",
) {
  return computeUnemployedRecommendedInvestment(
    allocatedData,
    employmentTypes,
    setaFields,
  );
}
